import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { dbService, userDisplayName } from '../../services/DatabaseService';
import { getAutoUserColor } from '../../utils/userColor';
import { ModernButton } from '../ui/ModernButton';
import './UserColorSettings.css';

// Quick-pick swatches shown above the full picker.
const PRESETS = [
  '#3B82F6', '#22C55E', '#EAB308', '#F97316', '#EF4444',
  '#EC4899', '#8B5CF6', '#14B8A6', '#64748B', '#0EA5E9'
];

const EyedropperIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

// Per-user color management. Colors are stored centrally in settings.userColors
// ({ userId: hex }); an unset user falls back to the deterministic auto palette.
// Saves are debounced and partial (they never touch the prompt settings).
export function UserColorSettings() {
  const [users, setUsers] = useState([]);
  const [colorMap, setColorMap] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [openId, setOpenId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(''); // '' | 'saved' | 'error'
  const [missingField, setMissingField] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [userList, settings] = await Promise.all([
          dbService.getUsers(),
          dbService.getSettings()
        ]);
        if (cancelled) return;
        setUsers(userList || []);
        setColorMap(settings?.userColors || {});
        setSavedMap(settings?.userColors || {});
      } catch (e) {
        console.error('Error loading user colors:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = JSON.stringify(colorMap) !== JSON.stringify(savedMap);

  // Close the open popover on outside click or Escape.
  useEffect(() => {
    if (!openId) return undefined;
    const onPointerDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpenId(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenId(null); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openId]);

  const setColor = (id, hex) => {
    setColorMap((prev) => ({ ...prev, [id]: hex }));
    setStatus('');
  };

  const resetColor = (id) => {
    setColorMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setStatus('');
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await dbService.saveUserColors(colorMap);
      if (res && res.success === false && res.error === 'missing-field') {
        setMissingField(true);
        setStatus('error');
      } else {
        setMissingField(false);
        setSavedMap(colorMap);
        setStatus('saved');
      }
    } catch (e) {
      console.error('Error saving user colors:', e);
      setStatus('error');
    } finally {
      setSaving(false);
    }
  }, [colorMap]);

  return (
    <div className="space-y-4" style={{ maxWidth: '34rem' }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Benutzerfarben</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Farbe pro Person für die Zuständigkeit-Kennzeichnung. Ohne eigene Farbe wird automatisch eine vergeben.
          </p>
        </div>
      </div>

      {missingField && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-sm text-amber-700 dark:text-amber-300">
          Das Feld <code>user_colors</code> fehlt in der PocketBase-Collection <code>settings</code>. Farben werden
          erst nach Anlegen des Feldes dauerhaft gespeichert.
        </div>
      )}

      <ul className="divide-y divide-slate-200 dark:divide-slate-700 rounded-xl border border-slate-200 dark:border-slate-700">
        {users.map((user) => {
          const custom = colorMap[user.id];
          const color = custom || getAutoUserColor(user);
          const isOpen = openId === user.id;
          return (
            <li key={user.id} className={`flex items-center gap-3 p-3 bg-white dark:bg-slate-800 ${isOpen ? 'relative z-40' : ''}`}>
              <span className="flex-1 font-medium text-slate-700 dark:text-slate-200">{userDisplayName(user)}</span>
              <span className="text-xs text-slate-400 dark:text-slate-500 hidden sm:inline">
                {custom ? 'Eigene Farbe' : 'Automatisch'}
              </span>

              {/* Color field = trigger; popover anchors here */}
              <div className="relative" ref={isOpen ? popoverRef : undefined}>
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : user.id)}
                  className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-slate-400 dark:hover:border-slate-500 transition-colors"
                  aria-haspopup="dialog"
                  aria-expanded={isOpen}
                  title="Farbe wählen"
                >
                  <span
                    className="rounded-md border border-black/10 dark:border-white/20"
                    style={{ width: 22, height: 22, backgroundColor: color }}
                  />
                  <span className="text-sm font-mono text-slate-700 dark:text-slate-200 uppercase">{color}</span>
                </button>

                {isOpen && (
                  <div
                    role="dialog"
                    className="absolute right-0 top-full mt-3 z-30 p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl w-[288px] uc-popover"
                  >
                    <div className="text-[11px] font-semibold tracking-wider text-slate-400 dark:text-slate-500 mb-2">SCHNELLAUSWAHL</div>
                    <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-slate-100 dark:border-slate-700">
                      {PRESETS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setColor(user.id, p)}
                          className="rounded-full border border-black/10 dark:border-white/20 hover:scale-110 transition-transform"
                          style={{ width: 20, height: 20, backgroundColor: p }}
                          aria-label={p}
                          title={p}
                        />
                      ))}
                    </div>

                    <HexColorPicker color={color} onChange={(hex) => setColor(user.id, hex)} />

                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-slate-400 dark:text-slate-500"><EyedropperIcon /></span>
                      <span className="flex-1 inline-flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 overflow-hidden">
                        <span className="px-1.5 text-slate-400 dark:text-slate-500 select-none">#</span>
                        <HexColorInput
                          color={color}
                          onChange={(hex) => setColor(user.id, hex)}
                          className="w-full px-1 py-1.5 bg-transparent text-slate-700 dark:text-slate-200 uppercase focus:outline-none text-sm"
                        />
                      </span>
                      <span className="rounded-full border border-black/10 dark:border-white/20" style={{ width: 26, height: 26, backgroundColor: color }} />
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => resetColor(user.id)}
                        disabled={!custom}
                        className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Automatisch
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenId(null)}
                        className="px-3 py-1 rounded-lg text-sm bg-green-600 text-white hover:bg-green-700"
                      >
                        Fertig
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </li>
          );
        })}
        {users.length === 0 && (
          <li className="p-4 text-sm text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800">
            Keine Benutzer gefunden.
          </li>
        )}
      </ul>

      <div className="flex items-center justify-end gap-4 pt-2">
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {dirty && !saving && 'Ungespeicherte Änderungen'}
          {!dirty && status === 'saved' && 'Gespeichert ✓'}
          {status === 'error' && !missingField && 'Fehler beim Speichern'}
        </span>
        <ModernButton
          onClick={handleSave}
          variant="success"
          size="lg"
          disabled={!dirty}
          loading={saving}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>}
        >
          Farben speichern
        </ModernButton>
      </div>
    </div>
  );
}
